import { useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, LinearProgress, Typography } from "@mui/material";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import FolderCopyRoundedIcon from "@mui/icons-material/FolderCopyRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { motion } from "framer-motion";
import { api } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { DataState } from "../components/Feedback";
import StatCard from "../components/StatCard";
import DonutCard from "../components/DonutCard";
import BarsCard from "../components/BarsCard";
import SectionCard from "../components/SectionCard";
import StatusChip from "../components/StatusChip";
import { EVIDENCE, RISK_LEVEL, TIER, WORKFLOW } from "../constants";
import { colorForLabel, complianceColor } from "../utils/colors";
import { firstName, greeting } from "../utils/format";
import { ANIMATE } from "../utils/motion";

const colorsOf = (map) => Object.fromEntries(Object.entries(map).map(([key, v]) => [key, v.color]));
const TIER_COLORS = colorsOf(TIER);
const WORKFLOW_COLORS = colorsOf(WORKFLOW);
const EVIDENCE_COLORS = colorsOf(EVIDENCE);
const LEVEL_COLORS = colorsOf(RISK_LEVEL);
const TIER_SHORT = Object.fromEntries(Object.entries(TIER).map(([key, v]) => [key, v.short]));

function Hero({ summary, name, onNavigate }) {
  const pct = summary.kpis.controlCompliancePct;
  return (
    <motion.div initial={ANIMATE ? { opacity: 0, y: 16 } : false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: 5,
          p: { xs: 3, md: 4 },
          color: "#fff",
          background: "linear-gradient(120deg, #312E81 0%, #5B21B6 45%, #0369A1 100%)",
          boxShadow: "0 20px 50px rgba(79,70,229,0.32)",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -80,
            top: -120,
            width: 340,
            height: 340,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.22), transparent 65%)",
          }}
        />
        <Box sx={{ position: "relative", maxWidth: 560 }}>
          <Typography sx={{ opacity: 0.8, fontWeight: 600 }}>
            {greeting()}
            {name ? `, ${name}` : ""}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, mb: 1 }}>
            {summary.clientName}
          </Typography>
          <Typography sx={{ opacity: 0.85, mb: 2.5 }}>
            Your governance and compliance posture at a glance: what is in use, how risky it is,
            and what still needs a decision.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={() => onNavigate("/ai-inventory")}
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{
                background: "#fff !important",
                color: "#312E81",
                boxShadow: "0 8px 20px rgba(0,0,0,0.2) !important",
              }}
            >
              Open applications
            </Button>
            <Button
              onClick={() => onNavigate("/risks")}
              sx={{ color: "#fff", border: "1px solid rgba(255,255,255,0.45)", "&:hover": { background: "rgba(255,255,255,0.12)" } }}
            >
              View risk register
            </Button>
          </Box>
        </Box>

        <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress variant="determinate" value={100} size={132} thickness={4} sx={{ color: "rgba(255,255,255,0.2)" }} />
            <CircularProgress
              variant="determinate"
              value={pct}
              size={132}
              thickness={4}
              sx={{ color: "#fff", position: "absolute", left: 0, "& .MuiCircularProgress-circle": { strokeLinecap: "round" } }}
            />
            <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
              <Box>
                <Typography sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>{pct}%</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  compliant
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Typography sx={{ fontWeight: 700 }}>Control compliance</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, maxWidth: 170 }}>
              Across all frameworks. Partially implemented controls count as half.
            </Typography>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

function FrameworkBars({ items }) {
  return (
    <SectionCard title="Framework compliance" subtitle="Share of applicable controls implemented" sx={{ height: "100%" }}>
      <Box sx={{ display: "grid", gap: 2.5 }}>
        {items.map((f) => {
          const color = complianceColor(f.compliancePct);
          return (
            <Box key={f.code}>
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {f.name}{" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    · {f.totalControls} controls
                  </Typography>
                </Typography>
                <Typography sx={{ fontWeight: 800, color }}>{f.compliancePct}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={f.compliancePct}
                sx={{
                  height: 10,
                  borderRadius: 99,
                  backgroundColor: "rgba(15,23,42,0.07)",
                  "& .MuiLinearProgress-bar": { borderRadius: 99, backgroundColor: color },
                }}
              />
            </Box>
          );
        })}
        {items.length === 0 && <Typography color="text.secondary">No frameworks added yet.</Typography>}
      </Box>
    </SectionCard>
  );
}

function TopRisks({ risks, onOpen }) {
  return (
    <SectionCard
      title="Top risks"
      subtitle="Highest scoring open risks"
      action={<Button size="small" onClick={onOpen}>View all</Button>}
      sx={{ height: "100%" }}
    >
      <Box sx={{ display: "grid", gap: 1.5 }}>
        {risks.map((r) => {
          const color = colorForLabel(RISK_LEVEL, r.levelLabel);
          return (
            <Box key={`${r.departmentId ?? ""}-${r.id}`} sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  flexShrink: 0,
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 800,
                  color: "#fff",
                  backgroundColor: color,
                }}
                title={`${r.levelLabel} risk, score ${r.score}`}
              >
                {r.score}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontWeight: 600 }}>
                  {r.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.department ? `${r.department} · ` : ""}{r.levelLabel} · {r.owner} · {r.statusLabel}
                </Typography>
              </Box>
            </Box>
          );
        })}
        {risks.length === 0 && <Typography color="text.secondary">No open risks.</Typography>}
      </Box>
    </SectionCard>
  );
}

function Attention({ apps, onOpen }) {
  return (
    <SectionCard
      title="Applications needing a decision"
      subtitle="Waiting for a risk assessment or for approval"
      action={<Button size="small" onClick={onOpen}>Open inventory</Button>}
      sx={{ height: "100%" }}
    >
      <Box sx={{ display: "grid", gap: 1.5 }}>
        {apps.map((a) => (
          <Box
            key={`${a.departmentId ?? ""}-${a.id}`}
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap" }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 600 }}>
                {a.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {a.department ? `${a.department} · ` : ""}{a.owner} · {a.riskTierLabel}
              </Typography>
            </Box>
            <StatusChip label={a.statusLabel} color={colorForLabel(WORKFLOW, a.statusLabel)} />
          </Box>
        ))}
        {apps.length === 0 && <Typography color="text.secondary">Nothing is waiting. Nice work.</Typography>}
      </Box>
    </SectionCard>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { clientId, authUser, me } = useSession();
  const state = useApiData(() => api.getDashboard(), [clientId]);

  return (
    <DataState state={state}>
      {(s) => {
        const k = s.kpis;
        return (
          <Box sx={{ display: "grid", gap: 3 }}>
            <Hero summary={s} name={firstName(authUser?.name || me.email)} onNavigate={navigate} />

            <Box
              sx={{
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(5, 1fr)" },
              }}
            >
              <StatCard index={0} icon={<SmartToyRoundedIcon />} label="Applications" value={k.totalApplications}
                hint="In the inventory" color="#4F46E5" onClick={() => navigate("/ai-inventory")} />
              <StatCard index={1} icon={<PendingActionsRoundedIcon />} label="Awaiting risk assessment" value={k.awaitingRiskAssessment}
                hint="Not yet assessed" color="#64748B"
                onClick={() => navigate("/ai-inventory?status=AWAITING_RISK_ASSESSMENT")} />
              <StatCard index={2} icon={<HowToRegRoundedIcon />} label="Awaiting approval" value={k.awaitingApproval}
                hint="Assessed, needs a decision" color="#8B5CF6"
                onClick={() => navigate("/ai-inventory?status=AWAITING_APPROVAL")} />
              <StatCard index={3} icon={<TaskAltRoundedIcon />} label="Approved" value={k.approvedApplications}
                hint="Cleared for use" color="#10B981"
                onClick={() => navigate("/ai-inventory?status=APPROVED")} />
              <StatCard index={4} icon={<WarningAmberRoundedIcon />} label="High & critical risks" value={k.highAndCriticalRisks}
                hint="Open in the risk register" color="#EF4444" onClick={() => navigate("/risks")} />
              <StatCard index={5} icon={<FolderCopyRoundedIcon />} label="Evidence needing attention" value={k.evidenceNeedingAttention}
                hint="Missing, expired or expiring" color="#F59E0B" onClick={() => navigate("/evidence")} />
              <StatCard index={6} icon={<GavelRoundedIcon />} label="Open audit findings" value={k.openFindings}
                hint="Not yet closed" color="#0EA5E9" onClick={() => navigate("/audits")} />
              <StatCard index={7} icon={<EventBusyRoundedIcon />} label="Policies overdue" value={k.policiesOverdueReview}
                hint="Past their review date" color="#F97316" onClick={() => navigate("/policies")} />
              <StatCard index={8} icon={<HealthAndSafetyRoundedIcon />} label="Vendors without a BAA" value={k.vendorsMissingBaa}
                hint="Handle PHI, no signed BAA" color="#DC2626" onClick={() => navigate("/vendors?baa=gap")} />
              <StatCard index={9} icon={<EventBusyRoundedIcon />} label="BAAs ending soon" value={k.baasExpiringSoon}
                hint="Within 90 days or already ended" color="#D97706" onClick={() => navigate("/vendors?baa=expiring")} />
            </Box>

            <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
              <DonutCard
                title="Applications by risk tier"
                subtitle="Click a slice to see those applications"
                slices={s.riskTierBreakdown}
                colors={TIER_COLORS}
                centerLabel="applications"
                onSliceClick={(key) => navigate(`/ai-inventory?tier=${key}`)}
              />
              <DonutCard
                title="Applications by approval stage"
                subtitle="From risk assessment through to approval"
                slices={s.workflowBreakdown}
                colors={WORKFLOW_COLORS}
                centerLabel="applications"
                onSliceClick={(key) => navigate(`/ai-inventory?status=${key}`)}
              />
            </Box>

            <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" } }}>
              <FrameworkBars items={s.frameworkCompliance} />
              <DonutCard
                title="Evidence health"
                subtitle="Is proof of control current?"
                slices={s.evidenceBreakdown}
                colors={EVIDENCE_COLORS}
                centerLabel="items"
                height={200}
              />
            </Box>

            <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "repeat(3, 1fr)" } }}>
              <BarsCard title="Open risks by level" subtitle="Likelihood × impact" slices={s.riskLevelBreakdown} colors={LEVEL_COLORS} />
              <BarsCard title="Open findings by severity" subtitle="From audits" slices={s.findingSeverityBreakdown} colors={LEVEL_COLORS} />
              <BarsCard title="Vendors by risk tier" subtitle={`${k.highRiskVendors} in Tier 1–2`} slices={s.vendorTierBreakdown} colors={TIER_COLORS} shortLabels={TIER_SHORT} />
            </Box>

            <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" } }}>
              <TopRisks risks={s.topRisks} onOpen={() => navigate("/risks")} />
              <Attention apps={s.applicationsNeedingAttention} onOpen={() => navigate("/ai-inventory")} />
            </Box>
          </Box>
        );
      }}
    </DataState>
  );
}
